/**
 * ClientLayout - Versicherten Portal Layout
 * Nordic Sanctuary design: Calm, accessible, reassuring
 * Per /DESIGN.md (Components) and /PRODUCT.md (Client App / patient guidance)
 */
import { useState } from "react";
import { LanguageSelector } from '@/components/LanguageSelector';
import { useTranslation } from "react-i18next";
import { useBrandLogoUrl } from "@/branding/BrandingProvider";
import { PrivacyNoticeModal } from "@/components/consent/PrivacyNoticeModal";
import { Link } from "@tanstack/react-router";
import { getBuildInfo } from "@/lib/buildInfo";

interface ClientLayoutProps {
  children: React.ReactNode;
  showLogo?: boolean;
  showFooter?: boolean;
}

export function ClientLayout({
  children,
  showLogo = true,
  showFooter = true,
}: ClientLayoutProps) {
  const { t } = useTranslation(["common", "techcheck", "video"]);
  const primaryLogoUrl = useBrandLogoUrl("primary");
  const [showPrivacy, setShowPrivacy] = useState(false);

  const privacyNoticeLabel = t("footer.privacyNotice");
  const privacyPolicyLabel = t("footer.privacyPolicy");
  const imprintLabel = t("footer.imprint");
  const buildInfo = getBuildInfo();
  const privacyTitle = t("privacy.title", { ns: "techcheck" });
  const privacyBody = t("privacy.body", { ns: "techcheck" });
  const closeLabel = t("settings.close", { ns: "video" });

  return (
    <div className="client-gradient-bg min-h-screen flex flex-col">
      <PrivacyNoticeModal
        open={showPrivacy}
        title={privacyTitle}
        body={privacyBody}
        acceptLabel={closeLabel}
        declineLabel={closeLabel}
        onAccept={() => setShowPrivacy(false)}
        onDecline={() => setShowPrivacy(false)}
      />
      {/* Header with Official Logo */}
      {showLogo && (
        <header className="relative z-10 p-6 flex justify-center">
          <img
            src={primaryLogoUrl}
            alt="jourfx"
            className="h-12 sm:h-14 w-auto"
          />
        </header>
      )}

      {/* Main Content */}
      <main className="relative z-10 flex-1 flex items-center justify-center p-4 sm:p-6">
        {children}
      </main>

      {/* Support Footer */}
      {showFooter && (
        <footer className="client-footer relative z-10">
          <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-2">
            <button
              type="button"
              onClick={() => setShowPrivacy(true)}
              className="client-footer-link font-medium"
            >
              {privacyNoticeLabel}
            </button>
            <span className="text-[var(--stone-300)]" aria-hidden="true">|</span>
            <Link to="/datenschutz" className="client-footer-link">
              {privacyPolicyLabel}
            </Link>
            <span className="text-[var(--stone-300)]" aria-hidden="true">|</span>
            <Link to="/impressum" className="client-footer-link">
              {imprintLabel}
            </Link>
          </div>
          <p className="mt-2 text-center text-xs text-[var(--stone-300)]">
            {t("footer.version", { version: buildInfo.appVersion })}
            {" · "}
            {t("footer.build", { buildId: buildInfo.buildId })}
          </p>
        </footer>
      )}

      {/* Language Selector - Fixed position */}
      <LanguageSelector />
    </div>
  );
}
